// Package imports:
import React, { useState } from 'react';
import { useField } from 'formik';
import cx from 'classnames';
import NumberFormat, { NumberFormatProps } from 'react-number-format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEye, faEyeSlash, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { faSearch } from '@fortawesome/pro-solid-svg-icons';

type InputType = 'input' | 'numberFormat' | 'textArea' | 'password';
type InputSizes = 'sm' | 'lg';
type TextAlign = 'left' | 'center' | 'right';

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
interface IOwnProps {
    inputType?: InputType,
    inputSize?: InputSizes,
    textAlign?: TextAlign,
    label?: string,
    shortLabel?: string,
    visible?: boolean,
    toggleVisible?(): void,
    withSearchGlassIcon?: boolean
};

type Props = IOwnProps & NumberFormatProps & InputProps & TextAreaProps;

const Input: React.FC<Props> = ({
    // Custom props
    inputType = 'input',
    inputSize = 'lg',
    textAlign = 'left',
    label,
    shortLabel,
    visible,
    toggleVisible,
    withSearchGlassIcon,
    // Element props
    id,
    className,
    style,
    onFocus,
    onBlur,
    value,
    disabled,
    ...props
}) => {
    const [field, meta] = useField(props.name ?? '');
    const [isFocused, setIsFocused] = useState(false);
    const focus = () => setIsFocused(true);
    const blur = () => setIsFocused(false);

    // Props that overwrite native element props.
    const elementProps = {
        id,
        value,
        disabled,
        style: {
            ...style,
            textAlign: textAlign
        },
        onFocus: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onFocus) onFocus(e);
            focus();
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement, Element>) => {
            if (onBlur) onBlur(e);
            blur();
        }
    }

    return (
        <div className={cx('KCL_input', inputSize, className, {
            'is-focused': isFocused,
            'has-value': (field.value !== undefined && field.value !== null && field.value.length > 0),
            'has-error': meta.error && meta.touched,
            'is-disabled': disabled
            })}
        >
            {inputType === 'numberFormat'
                ? <NumberFormat
                    {...elementProps}
                    {...(props as NumberFormatProps)}
                />
                : inputType === 'input'
                ? <>
                        <input
                            {...field}
                            {...elementProps}
                            {...(props as InputProps)}
                        />
                        {withSearchGlassIcon && <FontAwesomeIcon className='search-icon' icon={faSearch} />}
                        <FontAwesomeIcon icon={faCheck} className="icon-fa ok-icon" />
                        <FontAwesomeIcon icon={faTimesCircle} className="icon-fa error-icon" />
                        {meta.error && meta.touched && <div className="input-error-message">{meta.error}</div>}
                </>
                : inputType === 'textArea'
                ? <>
                        <textarea
                            {...field}
                            {...elementProps}
                            {...(props as TextAreaProps)}
                        />
                        {meta.error && meta.touched && <div className="input-error-message">{meta.error}</div>}
                </>
                : <>
                        <input
                            type={visible ? 'text' : 'password'}
                            {...field}
                            {...elementProps}
                            {...(props as InputProps)}
                        />
                        <FontAwesomeIcon icon={faCheck} className="icon-fa ok-icon" />
                        <FontAwesomeIcon icon={faTimesCircle} className="icon-fa error-icon" />
                        <FontAwesomeIcon icon={faEyeSlash} className={cx("icon-fa", "eye-icon", {'visible': !visible})} onClick={toggleVisible} />
                        <FontAwesomeIcon icon={faEye} className={cx("icon-fa", "eye-icon", {'visible': visible})} onClick={toggleVisible} />
                        {meta.error && meta.touched && <div className="input-error-message">{meta.error}</div>}
                </>
            }
            {label && (
                <label
                    className="input__label"
                    htmlFor={id}
                >
                    <span className="main-label">{label}</span>
                    <span className="short-label">{shortLabel ?? label}</span>
                </label>
            )}
        </div>
    );
}

export default (Input);